import { Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { DateRangeValue, getDaysAgoDate, getTimestamp, JsonObject, UiOption } from '../helpers';
import { get, post } from './sp-api.service';
import { endOfDay, startOfDay } from 'date-fns';
import { UserEventModel } from '../models/user-event/user-event.model';
import { toViolationApiKey } from '../models/user-event/helpers';
import { passErrorResponse } from '.';

type SearchFilter = {
  keyword?: string;
  users?: string[];
  decisions?: number[];
  violations?: string[];
  model?: string;
  date?: {
    start?: number;
    end?: number;
  };
  riskScore?: string[];
  services?: string[];
  referrerRelation?: string;
  referrers?: string[];
};

type CursorResponse = {
  nextCursor: string;
  previousCursor: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export const getUserEvents = async (
  cursor: string,
  pageSize: number,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<UserEventModel>> => {
  const filterParams = getSearchFilter(dtFilter);
  const sort = dtSort ? [{ [dtSort.columnName]: dtSort.direction }] : undefined;

  const result = (await post(
    '/telemetry/activity/cursor',
    {
      pageSize,
      cursor: cursor || undefined,
      sort,
      match: filterParams,
    },
    passErrorResponse
  )) as CursorResponse & { data: JsonObject[]; count: number };

  const { data, nextCursor, previousCursor } = result || {
    nextCursor: '',
    previousCursor: '',
  };

  if (!result || !Array.isArray(data)) {
    return {
      page: 0,
      pageSize,
      rows: [],
      total: 0,
      cursor: {
        current: cursor,
        next: '',
        previous: '',
      },
    };
  }

  const rows: UserEventModel[] = data.map((row) => new UserEventModel(row));

  return {
    page: 0,
    pageSize,
    rows,
    total: 0,
    cursor: {
      next: nextCursor,
      previous: previousCursor,
      current: cursor,
    },
  };
};

export const getUserActivityCount = async (dtFilter?: DtFilter): Promise<{ count: string }> => {
  const filterParams = getSearchFilter(dtFilter);
  const result = await post(
    '/telemetry/activity/count',
    { match: filterParams },
    passErrorResponse
  );
  const count = (result?.count as string) ?? '0';
  return { count };
};

export const getSearchFilter = (dtFilter?: DtFilter): SearchFilter => {
  const { keyword, values } = dtFilter || { keyword: '', values: [] };

  const searchFilter: SearchFilter = {};

  if (keyword) {
    searchFilter.keyword = keyword;
  }

  values.forEach(({ columnName, value }) => {
    switch (columnName) {
      case 'startTime':
        {
          const [startTime, endTime] = value as DateRangeValue;
          const startTimestamp = startTime ? getTimestamp(startOfDay(startTime), true) : null;
          const endTimestamp = endTime ? getTimestamp(endOfDay(endTime), true) : null;

          if (startTimestamp || endTimestamp) {
            const dateFilter: { start?: number; end?: number } = {};

            if (startTimestamp) {
              dateFilter.start = startTimestamp;
            }

            if (endTimestamp) {
              dateFilter.end = endTimestamp;
            }

            searchFilter.date = dateFilter;
          }
        }
        break;
      case 'action':
        {
          const safeValue = Array.isArray(value) ? value : [value];
          searchFilter.decisions = safeValue.map((val) => Number(val));
        }
        break;
      case 'model':
        searchFilter.model = value as string;
        break;
      case 'violation':
        searchFilter.violations = (value as string[]).map(toViolationApiKey);
        break;
      case 'user':
        searchFilter.users = [
          ...(searchFilter.users || []),
          ...(value as UiOption[]).map((option) => option.value),
        ];
        break;
      case 'riskLevel':
        searchFilter.riskScore = value as string[];
        break;
      case 'serviceId':
        searchFilter.services = [
          ...(searchFilter.services || []),
          ...(value as UiOption[]).map((option) => option.value),
        ];
        break;
      case 'referrerRelation':
        if (value) {
          searchFilter.referrerRelation = value as string;
        }
        break;
      case 'referrers':
        {
          const safeValue = Array.isArray(value) ? value : [value];
          searchFilter.referrers = safeValue.map((val) => val as string);
        }
        break;
    }
  });

  // Max allowed look-back period is 30 days
  const maxLookbackTime = getTimestamp(getDaysAgoDate(30), true)!;
  if (!searchFilter.date) {
    searchFilter.date = { start: 0 };
  }

  if (!searchFilter.date.start || searchFilter.date.start < maxLookbackTime) {
    searchFilter.date.start = maxLookbackTime;
  }

  if (searchFilter.date.end !== undefined && searchFilter.date.end < searchFilter.date.start) {
    searchFilter.date.end = searchFilter.date.start;
  }

  return searchFilter;
};

export const getUserEventById = async (eventId: string): Promise<UserEventModel | null> => {
  const response = await get(`/telemetry/activity/${eventId}`);
  return response ? new UserEventModel(response as JsonObject) : null;
};

export const getUserEventWithFullConversation = async (
  conversationId: string,
  cursor = '',
  anchorActivityId = ''
): Promise<CursorResponse & { userEvent: UserEventModel | null }> => {
  // cursor and anchorActivityId are mutually exclusive; the cursor wins once paging starts
  const response = await get(`/telemetry/activity/conversation/${conversationId}/cursor`, {
    pageSize: 10,
    cursor: cursor || undefined,
    anchorActivityId: cursor ? undefined : anchorActivityId || undefined,
  });

  if (!response) {
    return {
      nextCursor: '',
      previousCursor: '',
      hasNextPage: false,
      hasPreviousPage: false,
      userEvent: null,
    };
  }

  const { data, ...rest } = response as CursorResponse & { data: JsonObject };

  return {
    userEvent: new UserEventModel(data),
    ...rest,
  };
};

export const getUserEventWithPartialConversation = async (
  eventId: string
): Promise<UserEventModel | null> => {
  const response = await get(`/telemetry/activity/${eventId}/partial-conversation`);
  return response ? new UserEventModel(response as JsonObject) : null;
};

export const getUserTimeline = async (
  userId: string,
  prevCursor = ''
): Promise<{ rows: UserEventModel[]; nextCursor: string }> => {
  const { rows, cursor } = await getUserEvents(
    prevCursor,
    200,
    { columnName: 'startTime', direction: 'desc' },
    {
      keyword: '',
      fields: [],
      values: [
        {
          columnName: 'user',
          value: [{ label: '', value: userId }],
        },
      ],
    }
  );

  return { rows, nextCursor: cursor?.next ?? '' };
};
